/**
 * 「首次登入設定」（P1.5；產品負責人 2026-09-28）
 *
 * 情境：新帳號（`S0001`／`L0001`／`C0001`…）用**預設密碼**第一次登入之後，**唔可以就咁用** ——
 *   要即場設定：① 自己嘅 **email**（之後一律用 email ＋ 密碼登入）② **新密碼**
 *
 * 紅線（同後端一致；後端 `POST /api/auth/setup` 才係權威）
 *   · 一定要入**現有（預設）密碼** → 唔准淨係靠 session 就改走個帳號
 *   · email 全庫唯一（撞 → 409）；新密碼 ≥8 而且唔可以同預設一樣
 *   · 設定完成 → 撤銷自己其他 session（呢部留住）
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormTextField from '@coeldery/form-text-field';
import { btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type Me } from '../lib/api';

export default function Setup({ user, onDone }: { user: Me; onDone: (user: Me) => void }) {
  const { t } = useTranslation();
  /** `true` ＝ 首次登入（強制）／`false` ＝ 自己入嚟改（Email／密碼） */
  const first = Boolean(user.needsSetup);
  const [email, setEmail] = useState<string | null>('');
  const [current, setCurrent] = useState<string | null>('');
  const [next, setNext] = useState<string | null>('');
  const [confirm, setConfirm] = useState<string | null>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if ((next ?? '') !== (confirm ?? '')) {
      setError(t('setup.mismatch'));
      return;
    }
    setBusy(true);
    try {
      await api.setupAccount({
        email: (email ?? '').trim(),
        currentPassword: current ?? '',
        newPassword: next ?? '',
      });
      // 設定完 → 重新問一次「我係邊個」（`needsSetup` 應該變 false）
      const me = await api.me();
      onDone(me.user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ ...card, padding: '16px', maxWidth: '480px', margin: '24px auto 0' }}>
      <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800 }}>
        {first ? t('setup.title') : t('setup.titleChange')}
      </h2>
      <p style={hintStyle}>
        {first ? t('setup.intro', { name: user.username }) : t('setup.introChange', { name: user.username })}
      </p>

      <div style={{ marginTop: '12px' }}>
        <FormTextField id="f-setup-email" labelKey="setup.email" value={email} onChange={setEmail} />
        <p style={hintStyle}>{t('setup.emailHint')}</p>
        {!first && user.email && <p style={hintStyle}>{t('setup.currentEmail', { email: user.email })}</p>}
      </div>

      <div style={{ marginTop: '12px' }}>
        <FormTextField
          id="f-setup-current"
          labelKey={first ? 'setup.currentPassword' : 'setup.currentPasswordChange'}
          value={current}
          onChange={setCurrent}
          type="password"
        />
        <p style={hintStyle}>{t('setup.currentHint')}</p>
      </div>

      <div style={{ marginTop: '12px' }}>
        <FormTextField
          id="f-setup-new"
          labelKey={first ? 'setup.newPassword' : 'setup.newPasswordChange'}
          value={next}
          onChange={setNext}
          type="password"
        />
        <p style={hintStyle}>{t('setup.policy')}</p>
      </div>

      <div style={{ marginTop: '12px' }}>
        <FormTextField
          id="f-setup-confirm"
          labelKey="setup.confirm"
          value={confirm}
          onChange={setConfirm}
          type="password"
        />
      </div>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}

      <button
        type="button"
        style={{ ...btnPrimary, marginTop: '14px' }}
        onClick={() => void submit()}
        disabled={busy || !email || !current || !next}
      >
        {busy ? t('common.saving') : first ? t('setup.submit') : t('setup.submitChange')}
      </button>
    </section>
  );
}
